import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import useI18n from '../../i18n/useI18n'
import Table from '../../components/table'
import Pagination from '../../components/pagination'
import AgentsFilter from './filter'
import { BadgeCellRenderer, LogoCellRenderer } from '../../components/table-cell-renderers'
import ImageModal from '../../components/image-modal'
import useTableDataSync from '../../infrastructure/event-bus/hooks/use-table-data-sync'
import { Actions as ApiCallActions } from '../../redux/apiCall/reducers'
import EventBus from '../../infrastructure/event-bus/event-bus'
import general from '../../utils/general'
import RoleBasedAccess from '../../components/role-based-access'
import { UserTypeEnum } from '../../utils/enums'
import AgentDetailModal from './AgentDetailModal'
import StartContractModal from './StartContractModal'

const Agents = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { t } = useI18n()
  const [agents, setAgents] = useState([])
  const [loading, setLoading] = useState(true)
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1
  })

  const [filters, setFilters] = useState({
    search: '',
    city: '',
    country: '',
    has_active_contract: '',
    contract_expiring_soon: ''
  })

  const [isDetailModalOpen, setDetailModalOpen] = useState(false)
  const [detailAgent, setDetailAgent] = useState(null)
  const [isStartContractModalOpen, setStartContractModalOpen] = useState(false)
  const [contractAgent, setContractAgent] = useState(null)

  // Image Modal states
  const [imageModal, setImageModal] = useState({
    isOpen: false,
    imageUrl: '',
    imageAlt: ''
  })

  const buildQueryParams = (page, filterParams, limitOverride, sortParams = null) => {
    const queryParams = {
      page,
      limit: limitOverride || pagination.limit
    }

    if (filterParams.search && filterParams.search.trim()) {
      queryParams.search = filterParams.search.trim()
    }
    if (filterParams.city && filterParams.city.trim()) {
      queryParams.city = filterParams.city.trim()
    }
    if (filterParams.country && filterParams.country.trim()) {
      queryParams.country = filterParams.country.trim()
    }
    if (filterParams.has_active_contract !== '' && filterParams.has_active_contract !== null) {
      queryParams.has_active_contract = filterParams.has_active_contract === 'true'
    }
    if (filterParams.contract_expiring_soon !== '' && filterParams.contract_expiring_soon !== null) {
      queryParams.contract_expiring_soon = filterParams.contract_expiring_soon === 'true'
    }

    // Add sort parameters if provided
    if (sortParams && sortParams.sort && sortParams.order) {
      queryParams.sort = sortParams.sort
      queryParams.order = sortParams.order
    }

    return queryParams
  }

  const fetchAgents = (page = 1, filterParams = filters, limitOverride = null, sortParams = null) => {
    setLoading(true)

    dispatch(
      ApiCallActions.Get({
        url: 'agents',
        query: buildQueryParams(page, filterParams, limitOverride, sortParams),
        onSuccess: ({ data }) => {
          setAgents(data.data || [])
          setPagination({
            total: data.meta?.total || 0,
            page: data.meta?.page || 1,
            limit: data.meta?.limit || pagination.limit,
            totalPages: data.meta?.totalPages || 1
          })
          setLoading(false)
        },
        onError: () => {
          setAgents([])
          setLoading(false)
        }
      })
    )
  }

  useEffect(() => {
    fetchAgents(1, filters)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handlePageChange = (page) => {
    fetchAgents(page, filters)
  }

  const handleLimitChange = (newLimit) => {
    const updatedPagination = {
      ...pagination,
      limit: newLimit,
      page: 1
    }
    setPagination(updatedPagination)
    fetchAgents(1, filters, newLimit)
  }

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters)
  }

  const handleFilterSubmit = (newFilters) => {
    setFilters(newFilters)
    fetchAgents(1, newFilters)
  }

  const handleClearFilters = (clearedFilters) => {
    setFilters(clearedFilters)
    fetchAgents(1, clearedFilters)
  }

  const handleSort = (columnKey, direction) => {
    const sortParams = direction ? { sort: columnKey, order: direction } : null
    fetchAgents(pagination.page, filters, pagination.limit, sortParams)
  }

  const handleOpenDetailModal = (row) => {
    setDetailAgent(row)
    setDetailModalOpen(true)
  }

  const handleCloseDetailModal = () => {
    setDetailModalOpen(false)
    setDetailAgent(null)
  }

  const handleOpenStartContractModal = (row) => {
    setContractAgent(row)
    setStartContractModalOpen(true)
  }

  const handleCloseStartContractModal = () => {
    setStartContractModalOpen(false)
    setContractAgent(null)
  }

  const handleContractStarted = () => {
    fetchAgents(pagination.page, filters, pagination.limit)
  }

  const handleLogoClick = (imageUrl, imageAlt) => {
    if (imageUrl) {
      setImageModal({
        isOpen: true,
        imageUrl: imageUrl,
        imageAlt: imageAlt
      })
    }
  }

  const handleCloseModal = () => {
    setImageModal({
      isOpen: false,
      imageUrl: '',
      imageAlt: ''
    })
  }

  const handleDeleteClick = async (row) => {
    const agentName = row?.companyName || 
                     row?.name || 
                     `${row?.user?.firstName || ''} ${row?.user?.lastName || ''}`.trim() || 
                     t('AGENTS_ENTITY')
    const confirmed = await general.confirmDelete(t('AGENTS_ENTITY'), agentName)

    if (!confirmed) {
      return
    }

    dispatch(
      ApiCallActions.Delete({
        url: `agents/${row.id}`,
        showAlertOnError: true,
        showLoading: true,
        onSuccess: () => {
          EventBus.sc.onEntityDeleted({
            entityType: 'agent',
            entityId: row.id
          })
          general.showDeleteSuccess(t('AGENTS_ENTITY'))
        }
      })
    )
  }

  useTableDataSync(
    'agent',
    agents,
    setAgents,
    pagination,
    setPagination,
    (page, filterParams) => fetchAgents(page, filterParams || filters),
    filters
  )

  const getAgentName = (row) => {
    if (!row) return '-'
    if (row.companyName) return row.companyName
    if (row.name) return row.name
    if (row.fullName) return row.fullName
    const firstName = row?.user?.firstName || ''
    const lastName = row?.user?.lastName || ''
    const combined = `${firstName} ${lastName}`.trim()
    return combined || row?.user?.email || '-'
  }

  const getAgentEmail = (row) => {
    return row?.user?.email || row?.email || '-'
  }

  const tableColumns = [
    {
      key: 'logoUrl',
      label: t('AGENTS_TABLE_COLUMNS_LOGO'),
      sortable: false,
      render: (value, row) => {
        const logoUrl = value || row?.logoURL || row?.logo
        const agentName = getAgentName(row)
        return (
          <LogoCellRenderer
            imageUrl={logoUrl}
            imageAlt={agentName || t('AGENTS_TABLE_COLUMNS_LOGO')}
            onClick={handleLogoClick}
          />
        )
      }
    },
    {
      key: 'companyName',
      label: t('AGENTS_TABLE_COLUMNS_COMPANY_NAME'),
      sortable: true,
      render: (_, row) => getAgentName(row)
    },
    {
      key: 'email',
      label: t('AGENTS_TABLE_COLUMNS_EMAIL'),
      sortable: true,
      render: (_, row) => getAgentEmail(row)
    },
    {
      key: 'phone',
      label: t('AGENTS_TABLE_COLUMNS_PHONE'),
      sortable: true,
      render: (_, row) => row?.user?.phone || row?.phone || '-'
    },
    {
      key: 'city',
      label: t('AGENTS_TABLE_COLUMNS_CITY'),
      sortable: true,
      render: (value) => value || '-'
    },
    {
      key: 'country',
      label: t('AGENTS_TABLE_COLUMNS_COUNTRY'),
      sortable: true,
      render: (value) => value || '-'
    },
    {
      key: 'hasActiveContract',
      label: t('AGENTS_TABLE_COLUMNS_HAS_ACTIVE_CONTRACT'),
      sortable: true,
      render: (value, row) => (
        <BadgeCellRenderer
          value={row?.hasActiveContract || value}
          options={{
            trueLabel: t('AGENTS_TABLE_HAS_ACTIVE_CONTRACT_YES'),
            falseLabel: t('AGENTS_TABLE_HAS_ACTIVE_CONTRACT_NO'),
            trueClass: 'badge-success',
            falseClass: 'badge-danger'
          }}
        />
      )
    },
    {
      key: 'status',
      label: t('AGENTS_TABLE_COLUMNS_STATUS'),
      sortable: true,
      render: (_, row) => (
        <BadgeCellRenderer
          value={row?.user?.status === 'active'}
          options={{
            trueLabel: t('AGENTS_TABLE_STATUS_ACTIVE'),
            falseLabel: t('AGENTS_TABLE_STATUS_INACTIVE'),
            trueClass: 'badge-success',
            falseClass: 'badge-danger'
          }}
        />
      )
    }
  ]

  const renderActions = (row) => (
    <div className="d-flex align-items-center">
      <RoleBasedAccess allowedRoles={UserTypeEnum.ADMIN}>
        <button
          className="btn btn-sm btn-info btn-icon btn-round m-r-5"
          onClick={() => handleOpenDetailModal(row)}
          title={t('AGENTS_ACTIONS_DETAIL')}
        >
          <i className="zmdi zmdi-eye"></i>
        </button>
        <button
          className="btn btn-sm btn-warning btn-icon btn-round m-r-5"
          onClick={() => navigate(`/agents/${row.id}/edit`)}
          title={t('AGENTS_ACTIONS_EDIT')}
        >
          <i className="zmdi zmdi-edit"></i>
        </button>
        <button
          className="btn btn-sm btn-success btn-icon btn-round m-r-5"
          onClick={() => handleOpenStartContractModal(row)}
          title={t('AGENTS_ACTIONS_START_CONTRACT')}
        >
          <i className="zmdi zmdi-file-text"></i>
        </button>
        <button
          className="btn btn-sm btn-danger btn-icon btn-round"
          onClick={() => handleDeleteClick(row)}
          title={t('AGENTS_ACTIONS_DELETE')}
        >
          <i className="zmdi zmdi-delete"></i>
        </button>
      </RoleBasedAccess>
    </div>
  )

  return (
    <RoleBasedAccess allowedRoles={UserTypeEnum.ADMIN}>
      <section className="content">
        <div className="block-header">
          <div className="row">
            <div className="col-lg-7 col-md-6 col-sm-12 text-left">
              <h2>
                {t('AGENTS_LIST_TITLE')}
                <small>{t('AGENTS_LIST_SUBTITLE')}</small>
              </h2>
            </div>
            <div className="col-lg-5 col-md-6 col-sm-12">
              <RoleBasedAccess allowedRoles={UserTypeEnum.ADMIN}>
                <button 
                  className="btn btn-white btn-icon btn-round float-right m-l-10" 
                  type="button"
                  onClick={() => navigate('/agents/new')}
                >
                  <i className="zmdi zmdi-plus" title={t('AGENTS_ACTIONS_CREATE')}></i>
                </button>
              </RoleBasedAccess>
              <ul className="breadcrumb float-md-right">
                <li className="breadcrumb-item">
                  <a href="/dashboard">
                    <i className="zmdi zmdi-home" style={{ marginRight: '5px' }}></i> {t('COMMON_HOME')}
                  </a>
                </li>
                <li className="breadcrumb-item active">{t('AGENTS_LIST_TITLE')}</li>
              </ul>
            </div>
          </div>
        </div>
        <div className="container-fluid">
          <div className="row clearfix">
            <div className="col-lg-12">
              <AgentsFilter
                filters={filters}
                onFilterChange={handleFilterChange}
                onFilterSubmit={handleFilterSubmit}
                onClearFilters={handleClearFilters}
                onRefresh={() => fetchAgents(pagination.page, filters, pagination.limit)}
                loading={loading}
              />

              <Table
                columns={tableColumns}
                data={agents}
                loading={loading}
                emptyMessage={t('AGENTS_TABLE_EMPTY')}
                renderActions={renderActions}
                onSort={handleSort}
                pagination={pagination}
              />

              <AgentDetailModal
                isOpen={isDetailModalOpen}
                agent={detailAgent}
                onClose={handleCloseDetailModal}
              />

              <StartContractModal
                isOpen={isStartContractModalOpen}
                agent={contractAgent}
                onClose={handleCloseStartContractModal}
                onSuccess={handleContractStarted}
              />

              {/* Image Modal */}
              <ImageModal
                isOpen={imageModal.isOpen}
                imageUrl={imageModal.imageUrl}
                imageAlt={imageModal.imageAlt}
                onClose={handleCloseModal}
              />

              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.totalPages}
                total={pagination.total}
                limit={pagination.limit}
                onPageChange={handlePageChange}
                onLimitChange={handleLimitChange}
              />
            </div>
          </div>
        </div>
      </section>
    </RoleBasedAccess>
  )
}

export default Agents

